---
title: Review
description: The optional VLM pass, where a second model looks at flagged events.
---

Review is the last and only optional stage. A vision-language model running
on the same machine looks at the events analytics flagged and gives a
verdict. The doctrine in one line: **the CV decides when, the VLM decides
whether.** Verdicts enrich events in the report; they never change a count
or a dwell number.

The stage runs after perception has released the GPU, and it is non-fatal by
design: the report is already complete, so a down model server must not fail
the night. Only local endpoints are accepted; footage does not leave the
machine, and external AI APIs are refused.

## What gets flagged

Two triggers, both read from the night's events file:

- **Long dwell.** Any zone visit longer than the configured threshold, ten
  minutes by default.
- **Staff zones.** Any visit to a zone on the staff-only list. An empty
  list turns the trigger off.

Candidates are sorted longest first and capped per night, so a tracker
regression that mints thousands of phantom dwells cannot ship a whole night
of frames to the model. Events past the cap are recorded as skipped, never
silently dropped: the output must account for every candidate, so "not
verified" can never be confused with "not an event".

## Building the clip

For each event the stage cuts a short clip around the moment that triggered
it, using the cameras that actually saw that person, taken from the
journey data, not whichever cameras happen to sort first. Frames are
sampled at the configured rate, scaled down, and the total frame budget is
split across the chosen cameras. One practical rule from the field: with
many cameras a small frame budget spreads too thin, so prefer fewer
cameras at full temporal resolution. Each camera's extraction has a
timeout, because one corrupt video must not stall the whole nightly chain.

## Asking the question

Each event type has its own prompt template, filled with the store's
context: what kind of place this is and what objects matter. The frames and
the question go to the local model in one request, with temperature and
seed pinned so the same clip gets the same answer night over night.

The model must end its answer with a strict last line: `VERDICT: YES` or
`VERDICT: NO`. Only that final line decides. The strictness is deliberate:
matching the word "yes" anywhere in a reply lets reasoning like "yes, there
is a mannequin" confirm a false positive.

## What comes out

Every event ends in exactly one of five states, and the tally must account
for all of them:

| Verdict       | Meaning                                              |
| ------------- | ---------------------------------------------------- |
| `confirmed`   | The model looked and agreed                          |
| `rejected`    | The model looked and disagreed                       |
| `unparseable` | The model answered but broke the verdict contract    |
| `unverified`  | We could not look: no frames, or the endpoint failed |
| `skipped`     | Past the per-night cap                               |

The distinction between `rejected` and `unverified` is the point: "we
looked and it was nothing" and "we could not look" must never blur into
each other. Results are written next to the report with the model, settings
and per-event reasoning included, so a verdict is as traceable as a count.
